import React from 'react';
import { ChevronRight, X } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'src/shared/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'src/shared/components/ui/sheet';
import useCMSArticle from 'src/shared/hooks/cms/useCMSArticle';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import styles from './GnrWelcome.module.scss';

const METHODOLOGY_LIGHTBOX_IMAGE_URL = 'https://nm-cms-static.s3.eu-west-2.amazonaws.com/uat/physical_risk_store_a1d52fd987.png';

type MethodologySheetProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onStateOfNature?: () => void;
};

const MethodologySheet = ({ open, onOpenChange, onStateOfNature }: MethodologySheetProps) => {
    const [lightboxSrc, setLightboxSrc] = React.useState<string | null>(null);
    const methodologyArticle = useCMSArticle('physical-nature-risk-methodology');
    const { toGlobalNatureRiskStateOfNature } = useAppNavigation();

    const sheetTitleRef = React.useRef<HTMLHeadingElement>(null);
    const methodologyContentRef = React.useRef<HTMLDivElement>(null);

    const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target instanceof HTMLImageElement) {
            setLightboxSrc(METHODOLOGY_LIGHTBOX_IMAGE_URL);
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent
                    className='flex flex-col overflow-hidden'
                    side='right'
                    onOpenAutoFocus={e => {
                        e.preventDefault();
                        sheetTitleRef.current?.focus();
                    }}
                >
                    <SheetHeader>
                        <SheetTitle ref={sheetTitleRef} tabIndex={-1} className='text-xl font-normal focus:outline-none'>
                            {methodologyArticle?.title}
                        </SheetTitle>
                    </SheetHeader>
                    <div
                        ref={methodologyContentRef}
                        tabIndex={-1}
                        className='flex-1 overflow-y-auto py-4 text-base text-muted-foreground'
                        onClick={handleContentClick}
                    >
                        {methodologyArticle?.content && (
                            <div className={styles.content} dangerouslySetInnerHTML={{ __html: methodologyArticle.content }} />
                        )}
                    </div>
                    <div className='flex gap-2 pt-4 border-t'>
                        <SecondaryButton className='flex-1' data-testid='methodology-close-btn' onClick={() => onOpenChange(false)}>
                            Close
                        </SecondaryButton>
                        <PrimaryButton className='flex-1' onClick={onStateOfNature ?? toGlobalNatureRiskStateOfNature}>
                            State of Nature <ChevronRight className='inline h-4 w-4' />
                        </PrimaryButton>
                    </div>
                </SheetContent>
            </Sheet>
            <Dialog open={!!lightboxSrc} onOpenChange={open => !open && setLightboxSrc(null)}>
                <DialogContent
                    hideCloseButton
                    overlayClassName='bg-black/70'
                    className='flex max-h-[90vh] w-full max-w-3xl flex-col items-center justify-center overflow-hidden rounded-xl border-none bg-black/90 p-4'
                    onCloseAutoFocus={e => {
                        e.preventDefault();
                        methodologyContentRef.current?.focus();
                    }}
                >
                    <DialogTitle className='sr-only'>Image preview</DialogTitle>
                    <div className='flex w-full justify-end'>
                        <DialogClose data-testid='lightbox-close-btn' className='text-white opacity-70 hover:opacity-100'>
                            <X className='h-5 w-5' />
                            <span className='sr-only'>Close</span>
                        </DialogClose>
                    </div>
                    {lightboxSrc && (
                        <img
                            data-testid='lightbox-img'
                            src={lightboxSrc}
                            alt='Expanded view'
                            className='max-h-[80vh] max-w-full object-contain'
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MethodologySheet;
